import TaskAlt from "@mui/icons-material/TaskAlt";
import { Box, Stack, SvgIcon, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { sortBy } from "lodash-es";
import { type FC, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { PresetPollOptionsType } from "../../../../../poll/server/supabase-poll-service";
import type { PollWrapperSchema } from "../types";

export const PollFormSelectAnswerType: FC<{ presets: PresetPollOptionsType[] }> = ({ presets }) => {
  const { control, setValue } = useFormContext<PollWrapperSchema>();

  const setAnswerType = useCallback(
    (value: string) => {
      if (value === "custom") {
        setValue("poll.answer_type", {
          type: "custom",
          button_type: "primary_button",
          alignment: "vertical",
        });
        setValue("poll.options", []);
        return;
      }

      const preset = presets.find((p) => p.id === value);
      if (!preset) return;

      setValue("poll.answer_type", { type: "preset", variation: preset.id });
      setValue("poll.options", preset.options);
    },
    [setValue, presets],
  );

  return (
    <Controller
      name="poll.answer_type"
      control={control}
      render={({ field }) => (
        <ToggleButtonGroup
          value={field.value.type === "custom" ? "custom" : field.value.variation}
          exclusive
          onChange={(_, value: string) => setAnswerType(value)}
          fullWidth
        >
          <ToggleButton
            value="custom"
            sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}
            data-analytics-id="poll-answer-type-custom"
          >
            <Box>
              <TaskAlt />
            </Box>
            <Typography variant="body2" align="center">
              Custom Answers
            </Typography>
          </ToggleButton>
          {sortBy(presets, "order").map(({ id, name, icons }) => (
            <ToggleButton
              key={id}
              value={id}
              sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}
              data-analytics-id={`poll-answer-type-${id}`}
            >
              <Box>
                <PollIcon icons={icons} />
              </Box>
              <Typography variant="body2" align="center">
                {name}
              </Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    />
  );
};

const PollIcon: FC<{ icons: string[] | null }> = ({ icons }) => {
  if (!icons) return null;

  if (icons.length === 1) {
    return (
      <SvgIcon>
        <g dangerouslySetInnerHTML={{ __html: icons[0]! }} />
      </SvgIcon>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {icons.map((icon, index) => (
        <SvgIcon key={index}>
          <g dangerouslySetInnerHTML={{ __html: icon }} />
        </SvgIcon>
      ))}
    </Stack>
  );
};
